import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { rh } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, User2, MapPin, FileText, Briefcase, Landmark, CalendarRange,
  DoorOpen, ArrowLeft, LogOut, Plus, Save, Camera, Trash2, ShieldAlert, CheckCircle2,
  AlertTriangle,
} from "lucide-react";

/**
 * RH — Ficha de Registro do Colaborador
 * Conformidade: eSocial · CLT · LGPD · Portaria 671/2021 (MTE)
 * Layout: sidebar Navy com empresas + guia rápido + legenda de status
 *          form central branco com 8 seções em cascata.
 */

const SECOES = [
  { id: "empregador", num: "1", label: "Dados do Empregador", sub: "Empresa e contrato", icon: Building2 },
  { id: "pessoais", num: "2", label: "Dados Pessoais", sub: "Informações do colaborador", icon: User2 },
  { id: "contatos", num: "3", label: "Contatos e Endereço", sub: "Tel, email, endereço", icon: MapPin, alert: true },
  { id: "documentos", num: "4", label: "Documentação", sub: "CPF, RG, CTPS...", icon: FileText },
  { id: "contrato", num: "5", label: "Dados Contratuais", sub: "Salário e jornada", icon: Briefcase },
  { id: "banco", num: "6", label: "PIS / FGTS / Banco", sub: "Dados bancários", icon: Landmark },
  { id: "ferias", num: "7", label: "Férias e Ocorrências", sub: "Histórico e afastamentos", icon: CalendarRange },
  { id: "desligamento", num: "8", label: "Desligamento", sub: "Rescisão e saída", icon: DoorOpen, danger: true },
];

const LEGENDA = [
  { grupo: "ATIVO", color: "#10B981", itens: [
    { key: "ativo_normal", label: "Ativo Normal", dot: "#10B981" },
    { key: "ativo_atestado_15d", label: "Ativo c/ Atestado ≤15d", dot: "#22C55E" },
  ]},
  { grupo: "AFASTADO", color: "#F59E0B", itens: [
    { key: "afastado_inss", label: "Auxílio-Doença (INSS)", dot: "#F97316" },
    { key: "afastado_acidente", label: "Acidente de Trabalho", dot: "#EF4444" },
    { key: "afastado_maternidade", label: "Licença Maternidade", dot: "#EC4899" },
    { key: "afastado_militar", label: "Serviço Militar", dot: "#38BDF8" },
    { key: "afastado_lic_nao_remunerada", label: "Lic. Não Remunerada", dot: "#94A3B8" },
  ]},
  { grupo: "FÉRIAS", color: "#22D3EE", itens: [
    { key: "ferias_gozo", label: "Em Gozo de Férias", dot: "#22D3EE" },
  ]},
  { grupo: "SUSPENSO", color: "#EF4444", itens: [
    { key: "suspenso_disciplinar", label: "Suspensão Disciplinar", dot: "#DC2626" },
    { key: "suspenso_layoff", label: "Lay-off / Qualificação", dot: "#A855F7" },
  ]},
  { grupo: "DESLIGADO", color: "#6B7280", itens: [
    { key: "desligado_termino", label: "Término de Contrato", dot: "#6B7280" },
    { key: "desligado_sem_justa", label: "Demissão s/ Justa Causa", dot: "#6B7280" },
    { key: "desligado_pedido", label: "Pedido de Demissão", dot: "#6B7280" },
    { key: "desligado_justa_causa", label: "Demissão c/ Justa Causa", dot: "#6B7280" },
  ]},
];

const UF_LIST = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

const empty = {
  empresa_id: "",
  nome_completo: "", data_nascimento: "", estado_nascimento: "", cidade_nascimento: "",
  sexo: "", cor_raca: "", estado_civil: "", nacionalidade: "Brasil",
  grau_instrucao: "", deficiencia: "Nao", tipo_deficiencia: "",
  filiacao_mae: "", filiacao_pai: "", dependentes: [], foto: "",
  cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", uf: "",
  tel_residencial: "", celular_whatsapp: "", tel_recado: "", email_pessoal: "",
  rg_numero: "", rg_orgao: "", rg_uf: "", rg_emissao: "",
  cpf: "", ctps_numero: "", ctps_serie: "", ctps_uf: "", ctps_expedicao: "",
  titulo_numero: "", titulo_zona: "", titulo_secao: "",
  doc_militar_tipo: "Nao se aplica", doc_militar_numero: "",
  cnh_numero: "", cnh_categoria: "Sem CNH",
  conselho_nome: "Nao se aplica", conselho_numero: "",
  data_admissao: "", cargo: "", funcao: "", cbo: "", escala_horario: "", intervalo: "",
  salario: 0, base_calculo: "Mes", contribuicao_sindical: "Sim",
  pis_pasep: "", opcao_fgts_em: "", cadastro_fgts_em: "", data_retificacao_fgts: "",
  banco: "", tipo_conta: "Conta Corrente", agencia: "", conta: "",
  ferias_periodo_ini: "", ferias_periodo_fim: "", ferias_abono: "Nao",
  ferias_gozo_ini: "", ferias_gozo_fim: "",
  acidente_trabalho: "", advertencias: "", observacoes_gerais: "",
  status: "ativo_normal",
};

export default function RhFichaColaborador() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [empresaSel, setEmpresaSel] = useState(null);
  const [form, setForm] = useState({ ...empty });
  const [saving, setSaving] = useState(false);
  const [showDesligar, setShowDesligar] = useState(false);
  const [desligForm, setDesligForm] = useState({ tipo_desligamento: "", data_saida: "", data_rescisao: "", saldo_fgts_rescisorio: 0, observacoes: "" });
  const [confirmingDesl, setConfirmingDesl] = useState(false);
  const [activeSection, setActiveSection] = useState("empregador");
  const [id, setId] = useState(null);
  const [matricula, setMatricula] = useState("");

  useEffect(() => {
    rh.empresas().then(r => {
      setEmpresas(r.data);
      if (r.data.length && !empresaSel) {
        setEmpresaSel(r.data[0]);
        setForm(f => ({ ...f, empresa_id: r.data[0].id }));
      }
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const statusLabel = useMemo(() => {
    for (const g of LEGENDA) for (const it of g.itens) if (it.key === form.status) return it.label;
    return "Ativo Normal";
  }, [form.status]);

  const statusColor = useMemo(() => {
    for (const g of LEGENDA) for (const it of g.itens) if (it.key === form.status) return it.dot;
    return "#10B981";
  }, [form.status]);

  const scrollTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`sec-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const novoColaborador = () => {
    setForm({ ...empty, empresa_id: empresaSel?.id || "" });
    setId(null);
    setMatricula("");
    scrollTo("empregador");
    toast.success("Novo cadastro iniciado");
  };

  const salvar = async () => {
    if (!form.empresa_id) return toast.error("Selecione uma empresa contratante");
    if (!form.nome_completo) return toast.error("Nome completo é obrigatório");
    if (!form.email_pessoal) return toast.error("E-mail pessoal é obrigatório (credenciais de acesso)");
    setSaving(true);
    try {
      const payload = { ...form, salario: Number(form.salario) || 0 };
      const r = id ? await rh.editarColaborador(id, payload) : await rh.criarColaborador(payload);
      setId(r.data.id);
      setMatricula(r.data.matricula);
      toast.success(`Ficha salva. Matrícula: ${r.data.matricula}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const confirmarDesligamento = async () => {
    if (!id) return toast.error("Salve o cadastro antes de desligar");
    if (!desligForm.tipo_desligamento || !desligForm.data_saida) return toast.error("Tipo e data de saída são obrigatórios");
    setConfirmingDesl(true);
    try {
      const r = await rh.desligar(id, { ...desligForm, saldo_fgts_rescisorio: Number(desligForm.saldo_fgts_rescisorio) || 0 });
      toast.success(r.data.mensagem || "Desligamento confirmado. TI notificado.");
      setShowDesligar(false);
      const upd = await rh.colaborador(id);
      setForm(prev => ({ ...prev, status: upd.data.status }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao desligar");
    } finally {
      setConfirmingDesl(false);
    }
  };

  const addDependente = () => {
    setForm(f => ({ ...f, dependentes: [...(f.dependentes || []), { nome: "", parentesco: "", data_nascimento: "", cpf: "", ir: false, salario_familia: false }] }));
  };
  const rmDependente = (idx) => {
    setForm(f => ({ ...f, dependentes: f.dependentes.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* HEADER */}
      <header className="bg-gradient-to-r from-[#1A1D42] to-[#252952] border-b border-[#B28F4B]/30">
        <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24"><path d="M12 2L4 6v6c0 5 3.5 9.5 8 10 4.5-.5 8-5 8-10V6l-8-4z" stroke="#B28F4B" strokeWidth="1.5" fill="rgba(178,143,75,0.15)"/></svg>
            </div>
            <div>
              <div className="text-[#B28F4B] font-bold font-heading text-lg leading-tight">GRUPO SOEASY</div>
              <div className="text-white/60 text-[10px] uppercase tracking-widest">RH — Recursos Humanos / Departamento Pessoal</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/setores")} data-testid="btn-back-setores" className="px-3 py-1.5 rounded-md border border-[#B28F4B]/40 text-white hover:bg-[#B28F4B]/10 text-xs font-bold uppercase tracking-widest">Setores</button>
            <button onClick={() => { logout(); navigate("/login"); }} className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 text-xs font-bold uppercase tracking-widest">Sair</button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* SIDEBAR */}
        <aside className="w-72 bg-[#0A0B1A] border-r border-[#B28F4B]/20 shrink-0 max-h-[calc(100vh-56px)] overflow-y-auto sticky top-0">
          <div className="p-5 border-b border-[#B28F4B]/20">
            <div className="text-[#B28F4B] text-lg font-bold font-heading">RH / DEPTO. PESSOAL</div>
            <div className="text-white/50 text-[10px] uppercase tracking-widest mt-0.5">Ficha de Registro do Colaborador</div>
          </div>

          {/* Empresas contratantes */}
          <div className="p-4 border-b border-[#B28F4B]/15">
            <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3 flex items-center gap-1">
              <span>✦ Empresa Contratante</span>
            </div>
            <div className="space-y-2">
              {empresas.map(e => {
                const sel = empresaSel?.id === e.id;
                return (
                  <button
                    key={e.id}
                    onClick={() => { setEmpresaSel(e); set("empresa_id", e.id); }}
                    data-testid={`empresa-${e.nome.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                      sel ? "bg-[#1F2350] border-[#B28F4B] gold-glow"
                          : "bg-[#131532]/40 border-[#B28F4B]/15 hover:border-[#B28F4B]/40"
                    }`}
                  >
                    <div className="text-white text-xs font-bold uppercase tracking-wide">{e.nome}</div>
                    <div className="text-[#A1A4C7] text-[10px] font-mono mt-0.5 truncate">{e.cnpj}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Guia rápido - Seções */}
          <div className="p-4 border-b border-[#B28F4B]/15">
            <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3 flex items-center gap-1">
              <span>✦ Guia Rápido — Seções</span>
            </div>
            <div className="space-y-1.5">
              {SECOES.map(s => {
                const active = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    data-testid={`nav-secao-${s.id}`}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                      active ? "bg-[#1F2350] border-[#B28F4B]"
                             : "bg-transparent border-transparent hover:bg-[#131532]/40"
                    }`}
                  >
                    <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${active ? "text-[#B28F4B]" : s.danger ? "text-orange-300" : "text-white"}`}>
                      <s.icon size={11} /> {s.num}. {s.label.toUpperCase()}
                    </div>
                    <div className="text-[#6B6E8F] text-[9px] ml-4 mt-0.5">{s.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legenda */}
          <div className="p-4">
            <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">✦ Status — Legenda</div>
            {LEGENDA.map(g => (
              <div key={g.grupo} className="mb-3">
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: g.color }}>● {g.grupo}</div>
                {g.itens.map(it => (
                  <div key={it.key} className="flex items-center gap-2 text-[10px] text-[#A1A4C7] pl-3">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: it.dot }} />
                    <span>{it.label}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-[#B28F4B]/15 text-center">
            <div className="text-[#6B6E8F] text-[9px] font-mono uppercase tracking-widest">SOEASY RH · v1.0 · 2026</div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="flex-1 min-w-0 bg-slate-100">
          <div className="max-w-[1200px] mx-auto p-5">
            {/* Título + ações */}
            <div className="bg-white rounded-2xl border border-[#B28F4B]/15 shadow-sm p-5 mb-5 flex items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-[#1A1D42] font-heading text-xl font-bold">
                  📁 Ficha de Registro do Colaborador
                </div>
                <div className="text-slate-500 text-xs mt-1">
                  {empresaSel ? `${empresaSel.nome} · CNPJ: ${empresaSel.cnpj} · ${empresaSel.endereco}` : "Selecione a empresa contratante no painel à esquerda"}
                </div>
                {matricula && <div className="text-[#B28F4B] text-[10px] font-mono uppercase tracking-widest mt-1">Matrícula: {matricula}</div>}
              </div>
              <div className="flex items-center gap-2">
                <div className="px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5"
                  style={{ borderColor: statusColor + "60", color: statusColor, background: statusColor + "10" }}
                  data-testid="badge-status">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} /> {statusLabel}
                </div>
                <button onClick={novoColaborador} data-testid="btn-novo-colaborador" className="px-3 py-1.5 rounded-lg border border-[#B28F4B]/60 text-[#B28F4B] hover:bg-[#B28F4B]/10 text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                  <Plus size={13} /> Novo
                </button>
                <button onClick={salvar} disabled={saving} data-testid="btn-salvar-colaborador" className="px-4 py-1.5 rounded-lg bg-[#1A1D42] hover:bg-[#252952] text-white text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50">
                  <Save size={13} /> {saving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </div>

            {!empresaSel && (
              <EmptyState />
            )}

            {empresaSel && (
              <div className="space-y-5">
                <SecEmpregador empresa={empresaSel} matricula={matricula} form={form} set={set} />
                <SecPessoais form={form} set={set} onAddDep={addDependente} onRmDep={rmDependente} />
                <SecContatos form={form} set={set} />
                <SecDocumentos form={form} set={set} />
                <SecContrato form={form} set={set} />
                <SecBanco form={form} set={set} />
                <SecFerias form={form} set={set} />
                <SecDesligamento
                  form={form} desligForm={desligForm} setDeslig={setDesligForm}
                  showDesligar={showDesligar} setShowDesligar={setShowDesligar}
                  confirmar={confirmarDesligamento} confirming={confirmingDesl}
                  cadastroSalvo={!!id}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ---------- Empty state ---------- */
function EmptyState() {
  return (
    <div className="bg-white rounded-2xl border border-[#B28F4B]/15 shadow-sm p-16 text-center">
      <div className="text-6xl mb-4">🏢</div>
      <h2 className="text-[#1A1D42] font-heading text-xl font-bold mb-2">Selecione a Empresa Contratante</h2>
      <p className="text-slate-500 text-sm max-w-md mx-auto">
        Use o painel lateral esquerdo para selecionar a empresa do Grupo SOEASY que contratará este colaborador. Os dados do empregador serão preenchidos automaticamente.
      </p>
    </div>
  );
}

/* ---------- Componentes de campo reutilizáveis ---------- */
function Sec({ id, num, title, sub, icon: Icon, danger, children }) {
  return (
    <section id={`sec-${id}`} className="bg-white rounded-2xl border border-[#B28F4B]/15 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={18} className={danger ? "text-orange-500" : "text-[#B28F4B]"} />
        <h3 className="text-[#1A1D42] font-heading text-lg font-bold">{title}</h3>
      </div>
      <div className="text-slate-500 text-xs mb-4">{sub}</div>
      <div className="border-b border-[#B28F4B]/20 mb-5" />
      {children}
    </section>
  );
}

function F({ label, hint, children, cols = 1, className = "" }) {
  return (
    <div className={`${cols === 2 ? "md:col-span-2" : cols === 3 ? "md:col-span-3" : ""} ${className}`}>
      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-orange-500 mt-1">{hint}</div>}
    </div>
  );
}

const inp = "w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:border-[#B28F4B] focus:ring-1 focus:ring-[#B28F4B]/30 outline-none text-sm text-slate-800";
const sel = inp + " appearance-none";

/* ---------- 1. Empregador ---------- */
function SecEmpregador({ empresa, matricula, form, set }) {
  return (
    <Sec id="empregador" num="1" title="Dados do Empregador" sub="Preenchimento automático — vinculado à empresa selecionada no painel lateral" icon={Building2}>
      <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-5 mb-5">
        <F label="Foto do Colaborador">
          <div className="w-32 h-32 rounded-xl border-2 border-dashed border-[#B28F4B]/40 bg-slate-50 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-[#B28F4B] transition">
            <Camera size={26} />
            <div className="text-[10px] uppercase tracking-widest mt-1">Upload</div>
            <div className="text-[9px] text-slate-400 mt-0.5">JPG / PNG</div>
          </div>
        </F>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Empregador">
            <input value={empresa?.nome || ""} readOnly className={inp + " bg-slate-100"} />
          </F>
          <F label="CNPJ">
            <input value={empresa?.cnpj || ""} readOnly className={inp + " bg-slate-100"} />
          </F>
          <F label="Endereço da Empresa" cols={2}>
            <input value={empresa?.endereco || ""} readOnly className={inp + " bg-slate-100"} />
          </F>
          <F label="Matrícula / Código">
            <input value={matricula || "[Gerado ao Salvar]"} readOnly className={inp + " bg-slate-100 font-mono"} />
          </F>
          <F label="Autenticador">
            <input value="[Gerado ao Salvar]" readOnly className={inp + " bg-slate-100 font-mono text-[11px]"} />
          </F>
        </div>
      </div>
    </Sec>
  );
}

/* ---------- 2. Dados Pessoais ---------- */
function SecPessoais({ form, set, onAddDep, onRmDep }) {
  return (
    <Sec id="pessoais" num="2" title="Dados Pessoais" sub="Preenchimento pelo colaborador" icon={User2}>
      <div className="grid grid-cols-1 gap-4">
        <F label="Nome Completo">
          <input value={form.nome_completo} onChange={e => set("nome_completo", e.target.value)} placeholder="Nome completo conforme documentação" className={inp} data-testid="input-nome-completo" />
        </F>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="Data de Nascimento"><input type="date" value={form.data_nascimento} onChange={e => set("data_nascimento", e.target.value)} className={inp} /></F>
          <F label="Estado de Nascimento">
            <select value={form.estado_nascimento} onChange={e => set("estado_nascimento", e.target.value)} className={sel}>
              <option value="">Selecione</option>{UF_LIST.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </F>
          <F label="Cidade de Nascimento"><input value={form.cidade_nascimento} onChange={e => set("cidade_nascimento", e.target.value)} placeholder={form.estado_nascimento ? "Cidade" : "Selecione o estado primeiro"} className={inp} /></F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <F label="Sexo (eSocial)">
            <select value={form.sexo} onChange={e => set("sexo", e.target.value)} className={sel}>
              <option value="">Selecione</option><option>Masculino</option><option>Feminino</option><option>Não informar</option>
            </select>
          </F>
          <F label="Cor / Raça (IBGE)">
            <select value={form.cor_raca} onChange={e => set("cor_raca", e.target.value)} className={sel}>
              <option value="">Selecione</option><option>Branca</option><option>Preta</option><option>Parda</option><option>Amarela</option><option>Indígena</option><option>Não informada</option>
            </select>
          </F>
          <F label="Estado Civil">
            <select value={form.estado_civil} onChange={e => set("estado_civil", e.target.value)} className={sel}>
              <option value="">Selecione</option><option>Solteiro(a)</option><option>Casado(a)</option><option>Divorciado(a)</option><option>Viúvo(a)</option><option>União Estável</option>
            </select>
          </F>
          <F label="Nacionalidade">
            <select value={form.nacionalidade} onChange={e => set("nacionalidade", e.target.value)} className={sel}>
              <option>Brasil</option><option>Estrangeiro(a)</option>
            </select>
          </F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Grau de Instrução">
            <select value={form.grau_instrucao} onChange={e => set("grau_instrucao", e.target.value)} className={sel}>
              <option value="">Selecione</option>
              <option>Ensino Fundamental Incompleto</option><option>Ensino Fundamental Completo</option>
              <option>Ensino Médio Incompleto</option><option>Ensino Médio Completo</option>
              <option>Superior Incompleto</option><option>Superior Completo</option>
              <option>Pós-graduação</option><option>Mestrado</option><option>Doutorado</option>
            </select>
          </F>
          <F label="Possui Deficiência?">
            <select value={form.deficiencia} onChange={e => set("deficiencia", e.target.value)} className={sel}>
              <option>Não</option><option>Física</option><option>Auditiva</option><option>Visual</option><option>Mental</option><option>Intelectual</option><option>Múltipla</option><option>Reabilitado</option>
            </select>
          </F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Filiação — Nome da Mãe"><input value={form.filiacao_mae} onChange={e => set("filiacao_mae", e.target.value)} placeholder="Nome completo da mãe" className={inp} /></F>
          <F label="Filiação — Nome do Pai"><input value={form.filiacao_pai} onChange={e => set("filiacao_pai", e.target.value)} placeholder="Nome completo do pai" className={inp} /></F>
        </div>

        {/* Beneficiários */}
        <div className="pt-2">
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Beneficiários / Dependentes</label>
            <button onClick={onAddDep} data-testid="btn-add-dependente" className="px-2.5 py-1 rounded-md border border-[#B28F4B]/60 text-[#B28F4B] hover:bg-[#B28F4B]/10 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
              <Plus size={11} /> Adicionar
            </button>
          </div>
          {(!form.dependentes || form.dependentes.length === 0) && (
            <div className="text-slate-400 text-xs italic p-4 bg-slate-50 rounded-lg border border-slate-200">Nenhum dependente cadastrado.</div>
          )}
          {form.dependentes?.map((d, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px_140px_60px] gap-2 mb-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
              <input placeholder="Nome" value={d.nome} onChange={e => { const arr=[...form.dependentes]; arr[idx].nome=e.target.value; set("dependentes", arr); }} className={inp} />
              <select value={d.parentesco} onChange={e => { const arr=[...form.dependentes]; arr[idx].parentesco=e.target.value; set("dependentes", arr); }} className={sel}>
                <option value="">Parentesco</option><option>Cônjuge</option><option>Filho(a)</option><option>Enteado(a)</option><option>Pai/Mãe</option>
              </select>
              <input type="date" value={d.data_nascimento} onChange={e => { const arr=[...form.dependentes]; arr[idx].data_nascimento=e.target.value; set("dependentes", arr); }} className={inp} />
              <input placeholder="CPF" value={d.cpf} onChange={e => { const arr=[...form.dependentes]; arr[idx].cpf=e.target.value; set("dependentes", arr); }} className={inp} />
              <button onClick={() => onRmDep(idx)} className="rounded-md bg-red-500/10 text-red-500 hover:bg-red-500/20 flex items-center justify-center"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      </div>
    </Sec>
  );
}

/* ---------- 3. Contatos e Endereço ---------- */
function SecContatos({ form, set }) {
  return (
    <Sec id="contatos" num="3" title="Contatos e Endereço Residencial" sub="Preenchimento pelo colaborador" icon={MapPin}>
      <div className="grid grid-cols-1 gap-4">
        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_120px] gap-4">
          <F label="CEP"><input value={form.cep} onChange={e => set("cep", e.target.value)} placeholder="00000-000" className={inp} /></F>
          <F label="Logradouro / Rua"><input value={form.logradouro} onChange={e => set("logradouro", e.target.value)} placeholder="Preenchimento automático via CEP" className={inp} /></F>
          <F label="Nº"><input value={form.numero} onChange={e => set("numero", e.target.value)} placeholder="Nº" className={inp} /></F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <F label="Complemento"><input value={form.complemento} onChange={e => set("complemento", e.target.value)} placeholder="Apto, Bloco..." className={inp} /></F>
          <F label="Bairro"><input value={form.bairro} onChange={e => set("bairro", e.target.value)} placeholder="Via CEP" className={inp} /></F>
          <F label="Cidade"><input value={form.cidade} onChange={e => set("cidade", e.target.value)} placeholder="Via CEP" className={inp} /></F>
          <F label="UF">
            <select value={form.uf} onChange={e => set("uf", e.target.value)} className={sel}>
              <option value="">UF</option>{UF_LIST.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="Telefone Residencial"><input value={form.tel_residencial} onChange={e => set("tel_residencial", e.target.value)} placeholder="(00) 0000-0000" className={inp} /></F>
          <F label="Celular (WhatsApp)"><input value={form.celular_whatsapp} onChange={e => set("celular_whatsapp", e.target.value)} placeholder="(00) 0 0000-0000" className={inp} /></F>
          <F label="Telefone para Recado"><input value={form.tel_recado} onChange={e => set("tel_recado", e.target.value)} placeholder="(00) 0 0000-0000" className={inp} /></F>
        </div>
        <F label="E-mail Pessoal" hint="Obrigatório — credenciais de acesso">
          <input value={form.email_pessoal} onChange={e => set("email_pessoal", e.target.value)} placeholder="colaborador@gmail.com" className={inp} data-testid="input-email-pessoal" />
        </F>
      </div>
    </Sec>
  );
}

/* ---------- 4. Documentação ---------- */
function SecDocumentos({ form, set }) {
  return (
    <Sec id="documentos" num="4" title="Documentação" sub="Preenchimento pelo colaborador — documentos obrigatórios para admissão" icon={FileText}>
      <div className="space-y-6">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-sky-600 border-l-2 border-sky-500 pl-2 mb-2">🪪 Cédula de Identidade (RG)</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <F label="Nº do RG"><input value={form.rg_numero} onChange={e => set("rg_numero", e.target.value)} placeholder="Número" className={inp} /></F>
            <F label="Órgão Emissor">
              <select value={form.rg_orgao} onChange={e => set("rg_orgao", e.target.value)} className={sel}>
                <option value="">Selecione</option><option>SSP</option><option>DETRAN</option><option>DIC</option><option>DGPC</option><option>PC</option><option>Outro</option>
              </select>
            </F>
            <F label="UF Órgão">
              <select value={form.rg_uf} onChange={e => set("rg_uf", e.target.value)} className={sel}>
                <option value="">UF</option>{UF_LIST.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </F>
            <F label="Data Emissão"><input type="date" value={form.rg_emissao} onChange={e => set("rg_emissao", e.target.value)} className={inp} /></F>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-sky-600 border-l-2 border-sky-500 pl-2 mb-2">📇 CPF</div>
          <F label="CPF"><input value={form.cpf} onChange={e => set("cpf", e.target.value)} placeholder="000.000.000-00" className={inp} data-testid="input-cpf" /></F>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-sky-600 border-l-2 border-sky-500 pl-2 mb-2">📘 Carteira de Trabalho (CTPS)</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <F label="Nº CTPS"><input value={form.ctps_numero} onChange={e => set("ctps_numero", e.target.value)} placeholder="Número" className={inp} /></F>
            <F label="Série"><input value={form.ctps_serie} onChange={e => set("ctps_serie", e.target.value)} placeholder="Série" className={inp} /></F>
            <F label="UF CTPS">
              <select value={form.ctps_uf} onChange={e => set("ctps_uf", e.target.value)} className={sel}>
                <option value="">UF</option>{UF_LIST.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </F>
            <F label="Data Expedição"><input type="date" value={form.ctps_expedicao} onChange={e => set("ctps_expedicao", e.target.value)} className={inp} /></F>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-sky-600 border-l-2 border-sky-500 pl-2 mb-2">🗳️ Título Eleitoral</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Nº do Título"><input value={form.titulo_numero} onChange={e => set("titulo_numero", e.target.value)} placeholder="Número" className={inp} /></F>
            <F label="Zona Eleitoral"><input value={form.titulo_zona} onChange={e => set("titulo_zona", e.target.value)} placeholder="Zona" className={inp} /></F>
            <F label="Seção Eleitoral"><input value={form.titulo_secao} onChange={e => set("titulo_secao", e.target.value)} placeholder="Seção" className={inp} /></F>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-orange-600 border-l-2 border-orange-500 pl-2 mb-2">🎖️ Documento Militar (se aplicável)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Tipo">
              <select value={form.doc_militar_tipo} onChange={e => set("doc_militar_tipo", e.target.value)} className={sel}>
                <option>Nao se aplica</option><option>Certificado de Reservista</option><option>Certificado de Dispensa de Incorporação (CDI)</option><option>Certificado de Isenção</option>
              </select>
            </F>
            <F label="Nº Documento"><input value={form.doc_militar_numero} onChange={e => set("doc_militar_numero", e.target.value)} placeholder="Número" className={inp} /></F>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-red-600 border-l-2 border-red-500 pl-2 mb-2">🚗 CNH — Carteira Nacional de Habilitação</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Nº da CNH"><input value={form.cnh_numero} onChange={e => set("cnh_numero", e.target.value)} placeholder="Número" className={inp} /></F>
            <F label="Categoria">
              <select value={form.cnh_categoria} onChange={e => set("cnh_categoria", e.target.value)} className={sel}>
                <option>Sem CNH</option><option>ACC</option><option>A</option><option>B</option><option>AB</option><option>C</option><option>D</option><option>E</option>
              </select>
            </F>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-600 border-l-2 border-slate-500 pl-2 mb-2">🎓 Conselho de Classe (opcional)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Conselho">
              <select value={form.conselho_nome} onChange={e => set("conselho_nome", e.target.value)} className={sel}>
                <option>Nao se aplica</option><option>CREA</option><option>CRC</option><option>CRA</option><option>OAB</option><option>CRM</option><option>CRO</option><option>COREN</option><option>CRP</option><option>Outro</option>
              </select>
            </F>
            <F label="Nº de Inscrição"><input value={form.conselho_numero} onChange={e => set("conselho_numero", e.target.value)} placeholder="Número de registro" className={inp} /></F>
          </div>
        </div>
      </div>
    </Sec>
  );
}

/* ---------- 5. Dados Contratuais ---------- */
function SecContrato({ form, set }) {
  return (
    <Sec id="contrato" num="5" title="Dados Contratuais e Funcionais" sub="Preenchimento exclusivo do RH" icon={Briefcase}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <F label="Data de Admissão"><input type="date" value={form.data_admissao} onChange={e => set("data_admissao", e.target.value)} className={inp} /></F>
        <F label="Cargo">
          <select value={form.cargo} onChange={e => set("cargo", e.target.value)} className={sel}>
            <option value="">Selecione</option>
            <option>Vigilante</option><option>Supervisor de Vigilância</option><option>Controlador de Acesso</option>
            <option>Auxiliar de Limpeza</option><option>Recepcionista</option><option>Assistente Administrativo</option>
            <option>Analista de RH</option><option>Coordenador Operacional</option><option>Gerente</option>
          </select>
        </F>
        <F label="Função">
          <select value={form.funcao} onChange={e => set("funcao", e.target.value)} className={sel}>
            <option>Vinculada ao cargo</option><option>Chefia</option><option>Coordenação</option><option>Diretoria</option>
          </select>
        </F>
        <F label="CBO (automático)"><input value={form.cbo} onChange={e => set("cbo", e.target.value)} placeholder="Gerado após seleção do cargo" className={inp + " bg-slate-100"} readOnly /></F>
        <F label="Escala / Horário">
          <select value={form.escala_horario} onChange={e => set("escala_horario", e.target.value)} className={sel}>
            <option value="">Selecione</option>
            <option>12x36 Diurno</option><option>12x36 Noturno</option><option>6x1 Diurno</option><option>5x2 Diurno</option><option>Comercial (Seg-Sex)</option>
          </select>
        </F>
        <F label="Intervalo">
          <select value={form.intervalo} onChange={e => set("intervalo", e.target.value)} className={sel}>
            <option value="">Selecione</option><option>15 minutos</option><option>30 minutos</option><option>1 hora</option><option>2 horas</option>
          </select>
        </F>
        <F label="Salário (R$)"><input type="number" step="0.01" value={form.salario} onChange={e => set("salario", e.target.value)} placeholder="R$ 0,00" className={inp} data-testid="input-salario" /></F>
        <F label="Base de Cálculo">
          <select value={form.base_calculo} onChange={e => set("base_calculo", e.target.value)} className={sel}>
            <option>Mes</option><option>Hora</option><option>Dia</option><option>Semana</option><option>Quinzena</option>
          </select>
        </F>
        <F label="Contribuição Sindical">
          <select value={form.contribuicao_sindical} onChange={e => set("contribuicao_sindical", e.target.value)} className={sel}>
            <option>Sim</option><option>Não</option>
          </select>
        </F>
      </div>
    </Sec>
  );
}

/* ---------- 6. PIS / FGTS / Banco ---------- */
function SecBanco({ form, set }) {
  return (
    <Sec id="banco" num="6" title="PIS / FGTS / Dados Bancários" sub="Preenchimento pelo colaborador ou RH" icon={Landmark}>
      <div className="space-y-5">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-pink-600 border-l-2 border-pink-500 pl-2 mb-3">✦ PIS — PASEP e FGTS</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Nº do PIS / PASEP"><input value={form.pis_pasep} onChange={e => set("pis_pasep", e.target.value)} placeholder="Número do PIS" className={inp} /></F>
            <F label="Opção FGTS em"><input type="date" value={form.opcao_fgts_em} onChange={e => set("opcao_fgts_em", e.target.value)} className={inp} /></F>
            <F label="Cadastro FGTS em"><input type="date" value={form.cadastro_fgts_em} onChange={e => set("cadastro_fgts_em", e.target.value)} className={inp} /></F>
          </div>
          <div className="mt-3">
            <F label="Data Retificação FGTS"><input type="date" value={form.data_retificacao_fgts} onChange={e => set("data_retificacao_fgts", e.target.value)} className={inp + " md:w-64"} /></F>
          </div>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-600 border-l-2 border-emerald-500 pl-2 mb-3">🏦 Dados Bancários para Pagamento</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Banco">
              <select value={form.banco} onChange={e => set("banco", e.target.value)} className={sel}>
                <option value="">Selecione o banco</option>
                <option>Banco do Brasil (001)</option><option>Bradesco (237)</option><option>Caixa Econômica (104)</option>
                <option>Itaú (341)</option><option>Santander (033)</option><option>Nubank (260)</option>
                <option>Inter (077)</option><option>C6 Bank (336)</option><option>Sicredi (748)</option>
              </select>
            </F>
            <F label="Tipo de Conta">
              <select value={form.tipo_conta} onChange={e => set("tipo_conta", e.target.value)} className={sel}>
                <option>Conta Corrente</option><option>Conta Poupança</option><option>Conta Salário</option><option>Conta Digital</option>
              </select>
            </F>
            <F label="Agência (com dígito)"><input value={form.agencia} onChange={e => set("agencia", e.target.value)} placeholder="0000-0" className={inp} /></F>
            <F label="Número da Conta"><input value={form.conta} onChange={e => set("conta", e.target.value)} placeholder="00000000-0" className={inp} /></F>
          </div>
        </div>
      </div>
    </Sec>
  );
}

/* ---------- 7. Férias e Ocorrências ---------- */
function SecFerias({ form, set }) {
  return (
    <Sec id="ferias" num="7" title="Férias e Ocorrências" sub="Histórico atualizado pelo RH ao longo do vínculo" icon={CalendarRange}>
      <div className="space-y-5">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-600 border-l-2 border-amber-500 pl-2 mb-3">🏖️ Período de Férias</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Período Aquisitivo — Início"><input type="date" value={form.ferias_periodo_ini} onChange={e => set("ferias_periodo_ini", e.target.value)} className={inp} /></F>
            <F label="Período Aquisitivo — Fim"><input type="date" value={form.ferias_periodo_fim} onChange={e => set("ferias_periodo_fim", e.target.value)} className={inp} /></F>
            <F label="Abono Pecuniário">
              <select value={form.ferias_abono} onChange={e => set("ferias_abono", e.target.value)} className={sel}>
                <option>Nao</option><option>Sim (1/3)</option>
              </select>
            </F>
            <F label="Início do Gozo"><input type="date" value={form.ferias_gozo_ini} onChange={e => set("ferias_gozo_ini", e.target.value)} className={inp} /></F>
            <F label="Fim do Gozo"><input type="date" value={form.ferias_gozo_fim} onChange={e => set("ferias_gozo_fim", e.target.value)} className={inp} /></F>
          </div>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-red-600 border-l-2 border-red-500 pl-2 mb-3">⚠️ Ocorrências e Registros</div>
          <div className="space-y-3">
            <F label="Acidente de Trabalho">
              <textarea rows={3} value={form.acidente_trabalho} onChange={e => set("acidente_trabalho", e.target.value)} placeholder="Data, CID, circunstâncias, dias afastados, CAT emitida..." className={inp} />
            </F>
            <F label="Advertências">
              <textarea rows={3} value={form.advertencias} onChange={e => set("advertencias", e.target.value)} placeholder="Tipo (verbal/escrita), data, motivo, assinatura..." className={inp} />
            </F>
            <F label="Observações Gerais">
              <textarea rows={3} value={form.observacoes_gerais} onChange={e => set("observacoes_gerais", e.target.value)} placeholder="Registros relevantes do histórico do colaborador..." className={inp} />
            </F>
          </div>
        </div>
      </div>
    </Sec>
  );
}

/* ---------- 8. Desligamento ---------- */
function SecDesligamento({ form, desligForm, setDeslig, showDesligar, setShowDesligar, confirmar, confirming, cadastroSalvo }) {
  const ehDesligado = (form.status || "").startsWith("desligado");
  return (
    <Sec id="desligamento" num="8" title="Desligamento" sub="Preenchimento exclusivo do RH — encerramento definitivo do vínculo" icon={DoorOpen} danger>
      <div className="rounded-lg border border-orange-300 bg-orange-50 p-4 mb-4 flex gap-3">
        <ShieldAlert size={20} className="text-orange-500 shrink-0 mt-0.5" />
        <div className="text-sm text-orange-900">
          <div className="font-bold uppercase text-xs tracking-widest mb-1">⚠ Atenção — Ação Irreversível</div>
          <div className="text-[13px] leading-relaxed">
            O preenchimento desta seção encerra definitivamente o vínculo. O sistema irá <strong>notificar automaticamente o setor de TI</strong> para revogação imediata de todos os acessos do colaborador.
          </div>
        </div>
      </div>

      {ehDesligado ? (
        <div className="rounded-lg border-2 border-slate-300 bg-slate-100 p-6 text-center">
          <CheckCircle2 size={32} className="text-slate-500 mx-auto mb-2" />
          <div className="font-bold text-slate-700 uppercase tracking-widest text-sm">Colaborador Desligado</div>
          <div className="text-slate-500 text-xs mt-1">TI foi notificado automaticamente para revogação de acessos.</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <F label="Tipo de Desligamento">
              <select value={desligForm.tipo_desligamento} onChange={e => setDeslig(d => ({ ...d, tipo_desligamento: e.target.value }))} className={sel} data-testid="select-tipo-desligamento">
                <option value="">Selecione o motivo</option>
                <option value="termino_contrato">Término de Contrato</option>
                <option value="sem_justa_causa">Demissão s/ Justa Causa</option>
                <option value="pedido_demissao">Pedido de Demissão</option>
                <option value="justa_causa">Demissão c/ Justa Causa</option>
              </select>
            </F>
            <F label="Data de Saída"><input type="date" value={desligForm.data_saida} onChange={e => setDeslig(d => ({ ...d, data_saida: e.target.value }))} className={inp} /></F>
            <F label="Data da Rescisão / Homologação"><input type="date" value={desligForm.data_rescisao} onChange={e => setDeslig(d => ({ ...d, data_rescisao: e.target.value }))} className={inp} /></F>
            <F label="Saldo FGTS Rescisório (R$)"><input type="number" step="0.01" value={desligForm.saldo_fgts_rescisorio} onChange={e => setDeslig(d => ({ ...d, saldo_fgts_rescisorio: e.target.value }))} placeholder="R$ 0,00" className={inp} /></F>
          </div>
          <F label="Observações do Desligamento">
            <textarea rows={3} value={desligForm.observacoes} onChange={e => setDeslig(d => ({ ...d, observacoes: e.target.value }))} placeholder="Motivo detalhado, acordos, pendências de documentos e equipamentos..." className={inp} />
          </F>

          <div className="mt-5 flex items-center gap-2">
            <button
              onClick={confirmar}
              disabled={confirming || !cadastroSalvo}
              data-testid="btn-confirmar-desligamento"
              className="px-5 py-2.5 rounded-lg bg-gradient-to-br from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2 disabled:opacity-50"
            >
              <AlertTriangle size={14} /> {confirming ? "Confirmando..." : "Confirmar Desligamento"}
            </button>
            <button onClick={() => setDeslig({ tipo_desligamento: "", data_saida: "", data_rescisao: "", saldo_fgts_rescisorio: 0, observacoes: "" })} className="px-4 py-2.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 font-bold uppercase tracking-widest text-xs">
              Cancelar
            </button>
            {!cadastroSalvo && <span className="text-orange-500 text-xs">Salve o cadastro primeiro para poder desligar.</span>}
          </div>
        </>
      )}
    </Sec>
  );
}
